import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { CardService, CardData } from '../services/cardService';
import CardPreview from '../components/CardPreview';
import ColorPicker from '../components/ColorPicker';
import ProfilePhotoUpload from '../components/ProfilePhotoUpload';
import PortfolioGalleryUpload from '../components/PortfolioGalleryUpload';

// 앱 버전과 동일한 색상 옵션
const PREDEFINED_COLORS = [
  { name: 'pureBlack', value: '#000000' },
  { name: 'coolMint', value: '#00C2A8' },
  { name: 'deepBlue', value: '#1E3A8A' },
  { name: 'sunsetOrange', value: '#FF6B57' },
  { name: 'mutedViolet', value: '#7D6AFF' },
  { name: 'warmSand', value: '#F4D58D' },
  { name: 'modernBlue', value: '#3B82F6' },
  { name: 'vibrantGreen', value: '#10B981' },
  { name: 'coralRed', value: '#EF4444' },
  { name: 'royalPurple', value: '#8B5CF6' },
];

const BACKGROUND_COLORS = [
  { name: 'White', value: '#FFFFFF' },
  { name: 'Light Gray', value: '#F2F2F7' },
  { name: 'Light Blue', value: '#E6F3FF' },
  { name: 'Light Green', value: '#E6FFED' },
  { name: 'Light Pink', value: '#FFE6F0' },
  { name: 'Light Yellow', value: '#FFFACD' },
];

const CardEditorPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { cardId } = useParams<{ cardId: string }>();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cardData, setCardData] = useState<CardData | null>(null);

  // 폼 상태 (앱 버전과 동일한 필드들)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    position: '',
    website: '',
    customPath: '',
    address: '',
    bio: '',
    linkedin: '',
    twitter: '',
    instagram: '',
    // 앱 버전과 동일한 추가 필드들
    instagramId: '',
    threadsId: '',
    youtubeId: '',
    linkedinId: '',
    facebookId: '',
    tiktokId: '',
    kakaoChannelUrl: '',
    companyAddress: '',
    memo: '',
    introText: '',
    servicesIntro: '',
    calendlyLink: '',
    feedbackFormLink: '',
    // 색상 관련 필드들
    cardButtonColor: '#007AFF',
    cardBackgroundColor: '#FFFFFF',
    // 프로필 사진 필드 추가
    photoUrl: '',
    // 포트폴리오 갤러리 필드 추가
    portfolioImages: [] as string[]
  });

  // 안전한 번역 함수
  const safeT = (key: string, fallback: string) => {
    try {
      const result = t(key);
      return result === key ? fallback : result;
    } catch {
      return fallback;
    }
  };

  useEffect(() => {
    const loadCardData = async () => {
      if (!user?.uid || !cardId) {
        navigate('/dashboard');
        return;
      }

      try {
        setLoading(true);
        const card = await CardService.getCard(user.uid, cardId);
        
        if (!card) {
          alert(safeT('common.error.cardNotFound', 'Card not found.'));
          navigate('/dashboard');
          return;
        }

        setCardData(card);
                 setFormData({
           name: card.name || '',
           email: card.email || '',
           phone: card.phone || '',
           company: card.company || '',
           position: card.position || '',
           website: card.website || '',
                       customPath: card.customPath || '',
           address: card.address || '',
           bio: card.bio || '',
           linkedin: card.socialLinks?.linkedin || '',
           twitter: card.socialLinks?.twitter || '',
           instagram: card.socialLinks?.instagram || '',
           // 앱 버전과 동일한 추가 필드들
           instagramId: card.instagramId || '',
           threadsId: card.threadsId || '',
           youtubeId: card.youtubeId || '',
           linkedinId: card.linkedinId || '',
           facebookId: card.facebookId || '',
           tiktokId: card.tiktokId || '',
           kakaoChannelUrl: card.kakaoChannelUrl || '',
           companyAddress: card.companyAddress || '',
           memo: card.memo || '',
           introText: card.introText || '',
           servicesIntro: card.servicesIntro || '',
           calendlyLink: card.calendlyLink || '',
           feedbackFormLink: card.feedbackFormLink || '',
           // 색상 관련 필드들
           cardButtonColor: card.cardButtonColor || '#007AFF',
           cardBackgroundColor: card.cardBackgroundColor || '#FFFFFF',
           // 프로필 사진 필드
           photoUrl: card.photoUrl || '',
           // 포트폴리오 갤러리 필드
           portfolioImages: card.portfolioImages || []
         });
      } catch (error) {
        console.error('Failed to load card:', error);
        alert(safeT('cardEdit.errors.loadError', 'An error occurred while loading card information.'));
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };

    loadCardData();
  }, [user?.uid, cardId, navigate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    if (!user?.uid || !cardId || !cardData) return;

    if (!formData.name.trim() || !formData.email.trim()) {
      alert(safeT('common.alert.missingInfoMessage', 'Please enter your name and email.'));
      return;
    }

    if (!formData.customPath.trim()) {
      alert(safeT('customUrl.required', 'Please enter a custom URL.'));
      return;
    }

    setSaving(true);
    try {
             const updatedCard: Partial<CardData> = {
         name: formData.name,
         email: formData.email,
         phone: formData.phone || '',
         company: formData.company || '',
         position: formData.position || '',
                   website: formData.website || '',
          customPath: formData.customPath || '',
          address: formData.address || '',
         bio: formData.bio || '',
         // 앱 버전과 동일한 추가 필드들
         instagramId: formData.instagramId || '',
         threadsId: formData.threadsId || '',
         youtubeId: formData.youtubeId || '',
         linkedinId: formData.linkedinId || '',
         facebookId: formData.facebookId || '',
         tiktokId: formData.tiktokId || '',
         kakaoChannelUrl: formData.kakaoChannelUrl || '',
         companyAddress: formData.companyAddress || '',
         memo: formData.memo || '',
         introText: formData.introText || '',
         servicesIntro: formData.servicesIntro || '',
         calendlyLink: formData.calendlyLink || '',
         feedbackFormLink: formData.feedbackFormLink || '',
         // 색상 관련 필드들
         cardButtonColor: formData.cardButtonColor,
         cardBackgroundColor: formData.cardBackgroundColor,
         // 프로필 사진 및 포트폴리오 갤러리
         photoUrl: formData.photoUrl || '',
         portfolioImages: formData.portfolioImages || [],
         socialLinks: {
           linkedin: formData.linkedin || '',
           twitter: formData.twitter || '',
           instagram: formData.instagram || ''
         }
       };

      await CardService.updateCard(user.uid, cardId, updatedCard);
      
      alert(safeT('common.saveSuccess', 'Card saved successfully.'));
      navigate('/dashboard');
    } catch (error) {
      console.error('Failed to save card:', error);
      alert(safeT('cardEdit.errors.saveError', 'An error occurred while saving.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!user?.uid || !cardId || !cardData) return;

    if (!confirm(safeT('cardEdit.deleteConfirm', 'Are you sure you want to delete this card?'))) {
      return;
    }

    try {
      await CardService.deleteCard(user.uid, cardId);
      alert(safeT('cardEdit.deleteSuccess', 'Card deleted successfully.'));
      navigate('/dashboard');
    } catch (error) {
      console.error('Failed to delete card:', error);
      alert(safeT('cardEdit.deleteError', 'Error occurred while deleting the card.'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{safeT('cardEdit.loading', 'Loading card information...')}</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">{safeT('auth.loginRequired', 'Login required.')}</p>
          <button
            onClick={() => navigate('/login')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
          >
            {safeT('auth.login', 'Login')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* 헤더 */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold text-gray-900">
              {safeT('cardEdit.title', 'Edit Card')}
            </h1>
            <button
              onClick={() => navigate('/dashboard')}
              className="text-gray-600 hover:text-gray-800"
            >
              ✕ {safeT('common.cancel', 'Cancel')}
            </button>
          </div>
        </div>

        {/* 메인 컨테이너 - 미리보기와 폼을 나란히 배치 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* 미리보기 */}
          <div className="lg:sticky lg:top-8 lg:h-fit">
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                             <h2 className="text-lg font-semibold text-gray-900 mb-4">
                 {safeT('profilePreview.title', 'Preview')}
               </h2>
                             <CardPreview
                 cardData={{
                   name: formData.name,
                   company: formData.company,
                   position: formData.position,
                   email: formData.email,
                   phone: formData.phone,
                   website: formData.website,
                                       customPath: formData.customPath,
                   photoUrl: formData.photoUrl,
                   portfolioImages: formData.portfolioImages
                 }}
                 cardButtonColor={formData.cardButtonColor}
                 cardBackgroundColor={formData.cardBackgroundColor}
               />
            </div>
          </div>

          {/* 폼 */}
          <div>
            <div className="bg-white shadow rounded-lg">
          <div className="p-6 space-y-6">
                         {/* 프로필 사진 업로드 */}
             <div className="mb-6">
               <h3 className="text-lg font-medium text-gray-900 mb-4">
                 {safeT('cardEdit.profilePhoto', 'Profile Photo')}
               </h3>
               <ProfilePhotoUpload
                 currentPhotoUrl={formData.photoUrl}
                 onPhotoChange={(photoUrl) => setFormData(prev => ({ ...prev, photoUrl }))}
                 userId={user.uid}
                 disabled={saving}
               />
             </div>



             {/* 기본 정보 */}
             <div>
               <h3 className="text-lg font-medium text-gray-900 mb-4">
                 {safeT('cardEdit.basicInfo', 'Basic Information')}
               </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                  <label className="block text-sm font-medium text-red-600 mb-1">
                    {safeT('cardEdit.name', 'Name')} **
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    onInvalid={(e) => {
                      const target = e.target as HTMLInputElement;
                      target.setCustomValidity(safeT('paywall.required', 'Required'));
                    }}
                    onInput={(e) => {
                      const target = e.target as HTMLInputElement;
                      target.setCustomValidity('');
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                         placeholder={safeT('placeholders.name', 'Example: John Doe')}
                  />
                </div>
                                <div>
                  <label className="block text-sm font-medium text-red-600 mb-1">
                    {safeT('cardEdit.email', 'Email')} **
                  </label>
                                    <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    onInvalid={(e) => {
                      const target = e.target as HTMLInputElement;
                      if (target.validity.valueMissing) {
                        target.setCustomValidity(safeT('paywall.required', 'Required'));
                      } else if (target.validity.typeMismatch) {
                        target.setCustomValidity(safeT('auth.emailInvalid', 'Please enter a valid email address.'));
                      }
                    }}
                    onInput={(e) => {
                      const target = e.target as HTMLInputElement;
                      target.setCustomValidity('');
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                         placeholder="example@email.com"
                  />
                </div>
                <div>
                                     <label className="block text-sm font-medium text-gray-700 mb-1">
                     {safeT('cardEdit.phone', 'Phone')}
                   </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="010-1234-5678"
                  />
                </div>
                <div>
                                     <label className="block text-sm font-medium text-gray-700 mb-1">
                     {safeT('cardEdit.website', 'Website')}
                   </label>
                  <input
                    type="text"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={safeT('placeholders.website', 'example.com')}
                  />
                </div>
              </div>
            </div>

            {/* 커스텀 URL */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {safeT('customUrl.title', 'Custom URL')}
              </h3>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-red-600 mb-1">
                    {safeT('customUrl.placeholder', 'Custom URL')} **
                  </label>
                  <div className="flex">
                    <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                      scanvcf.com/
                    </span>
                    <input
                      type="text"
                                             name="customPath"
                       value={formData.customPath}
                      onChange={handleInputChange}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-r-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder={safeT('customUrl.placeholder', 'yourname')}
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    {safeT('customUrl.helpText', '3-30 characters, lowercase letters, numbers and hyphens only.')}
                  </p>
                </div>
              </div>
            </div>

            {/* 직장 정보 */}
            <div>
                             <h3 className="text-lg font-medium text-gray-900 mb-4">
                 {safeT('cardEdit.contactInfo', 'Work Information')}
               </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                                     <label className="block text-sm font-medium text-gray-700 mb-1">
                     {safeT('cardEdit.company', 'Company')}
                   </label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                         placeholder={safeT('placeholders.company', 'Company Name')}
                  />
                </div>
                <div>
                                     <label className="block text-sm font-medium text-gray-700 mb-1">
                     {safeT('cardEdit.jobTitle', 'Position')}
                   </label>
                  <input
                    type="text"
                    name="position"
                    value={formData.position}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                         placeholder={safeT('placeholders.jobTitle', 'Job Title')}
                  />
                </div>
                <div className="md:col-span-2">
                                     <label className="block text-sm font-medium text-gray-700 mb-1">
                     {safeT('cardEdit.address', 'Address')}
                   </label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                         placeholder={safeT('placeholders.address', 'Address')}
                  />
                </div>
              </div>
            </div>

            {/* 소셜 미디어 */}
            <div>
                             <h3 className="text-lg font-medium text-gray-900 mb-4">
                 {safeT('cardEdit.socialMedia', 'Social Media')}
               </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">
                     {safeT('cardEdit.instagram', 'Instagram')}
                   </label>
                  <input
                    type="text"
                    name="instagramId"
                    value={formData.instagramId}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={safeT('placeholders.instagramId', '@username')}
                  />
                </div>
                                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">
                     {safeT('cardEdit.threads', 'Threads')}
                   </label>
                  <input
                    type="text"
                    name="threadsId"
                    value={formData.threadsId}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={safeT('placeholders.threadsId', '@username')}
                  />
                </div>
                                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">
                     {safeT('cardEdit.youtube', 'YouTube')}
                   </label>
                  <input
                    type="text"
                    name="youtubeId"
                    value={formData.youtubeId}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={safeT('placeholders.youtubeId', '@username')}
                  />
                </div>
                                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">
                     {safeT('cardEdit.linkedin', 'LinkedIn')}
                   </label>
                  <input
                    type="text"
                    name="linkedinId"
                    value={formData.linkedinId}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={safeT('placeholders.linkedinId', 'username')}
                  />
                </div>
                                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">
                     {safeT('cardEdit.facebook', 'Facebook')}
                   </label>
                  <input
                    type="text"
                    name="facebookId"
                    value={formData.facebookId}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={safeT('placeholders.facebookId', 'username')}
                  />
                </div>
                                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">
                     {safeT('cardEdit.tiktok', 'TikTok')}
                   </label>
                  <input
                    type="text"
                    name="tiktokId"
                    value={formData.tiktokId}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={safeT('placeholders.tiktokId', '@username')}
                  />
                </div>
                                 <div className="md:col-span-2">
                   <label className="block text-sm font-medium text-gray-700 mb-1">
                     {safeT('cardEdit.kakao', 'KakaoTalk Channel')}
                   </label>
                  <input
                    type="text"
                    name="kakaoChannelUrl"
                    value={formData.kakaoChannelUrl}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={safeT('placeholders.kakaoChannelUrl', '채널 URL')}
                  />
                </div>
              </div>
            </div>

            {/* AI Introduction */}
            <div>
                             <h3 className="text-lg font-medium text-gray-900 mb-4">
                 {safeT('cardEdit.companyIntro', 'Company Introduction')}
               </h3>
              <div className="space-y-4">
                <div>
                                     <label className="block text-sm font-medium text-gray-700 mb-1">
                     {safeT('cardEdit.selfIntro', 'Self Introduction')}
                   </label>
                  <textarea
                    name="introText"
                    value={formData.introText}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                         placeholder={safeT('placeholders.selfIntro', 'Enter your self-introduction...')}
                  />
                </div>
                <div>
                                     <label className="block text-sm font-medium text-gray-700 mb-1">
                     {safeT('cardEdit.serviceIntro', 'Service Introduction')}
                   </label>
                  <textarea
                    name="servicesIntro"
                    value={formData.servicesIntro}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                         placeholder={safeT('placeholders.serviceIntro', 'Enter your service introduction...')}
                  />
                </div>
              </div>
            </div>

            {/* Memo */}
            <div>
                             <label className="block text-sm font-medium text-gray-700 mb-1">
                 {safeT('cardEdit.memo', 'Memo')}
               </label>
              <textarea
                name="memo"
                value={formData.memo}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                     placeholder={safeT('placeholders.memo', 'Enter your memo...')}
              />
            </div>

            {/* Advanced Web Features */}
            <div>
                             <h3 className="text-lg font-medium text-gray-900 mb-4">
                 {safeT('cardEdit.miniWebsite', 'Mini Website')}
               </h3>
              <div className="space-y-4">
                <div>
                                     <label className="block text-sm font-medium text-gray-700 mb-1">
                     {safeT('cardEdit.bookingLink', 'Booking Link')}
                   </label>
                  <input
                    type="text"
                    name="calendlyLink"
                    value={formData.calendlyLink}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={safeT('placeholders.calendlyLink', 'calendly.com/yourusername')}
                  />
                                     <p className="text-sm text-gray-500 mt-1">{safeT('cardEdit.bookingTip', 'Enter booking links such as Calendly')}</p>
                </div>
                <div>
                                     <label className="block text-sm font-medium text-gray-700 mb-1">
                     {safeT('cardEdit.reviewLink', 'Review Link')}
                   </label>
                  <input
                    type="text"
                    name="feedbackFormLink"
                    value={formData.feedbackFormLink}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={safeT('placeholders.feedbackFormLink', 'forms.gle/example')}
                  />
                                     <p className="text-sm text-gray-500 mt-1">{safeT('cardEdit.reviewTip', 'Enter review links such as Google Forms')}</p>
                </div>
              </div>
            </div>

            {/* 포트폴리오 갤러리 */}
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {safeT('cardEdit.portfolioImages', 'Portfolio Images')}
              </h3>
              <PortfolioGalleryUpload
                currentImages={formData.portfolioImages}
                onImagesChange={(portfolioImages) => setFormData(prev => ({ ...prev, portfolioImages }))}
                userId={user.uid}
                disabled={saving}
              />
            </div>



                         {/* 색상 선택 */}
                         <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {safeT('cardColorSelection', 'Color Selection')}
              </h3>
               <div className="space-y-6">
                 {/* 버튼 색상 선택 */}
                 <div>
                   <ColorPicker
                     color={formData.cardButtonColor}
                     onColorChange={(color) => setFormData(prev => ({ ...prev, cardButtonColor: color }))}
                     showSwatches={true}
                     swatches={PREDEFINED_COLORS}
                     label={safeT('cardEdit.buttonColor', 'Button Color')}
                   />
                 </div>

                 {/* 배경 색상 선택 */}
                 <div>
                   <ColorPicker
                     color={formData.cardBackgroundColor}
                     onColorChange={(color) => setFormData(prev => ({ ...prev, cardBackgroundColor: color }))}
                     showSwatches={true}
                     swatches={BACKGROUND_COLORS}
                     label={safeT('cardEdit.backgroundColor', 'Background Color')}
                   />
                 </div>
               </div>
             </div>
          </div>

          {/* 액션 버튼들 */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between">
            <button
              onClick={handleDelete}
              className="px-4 py-2 border border-red-300 text-red-600 rounded-md hover:bg-red-50"
            >
              {safeT('cardEdit.delete', 'Delete')}
            </button>
            <div className="flex space-x-3">
              <button
                onClick={() => navigate('/dashboard')}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                {safeT('common.cancel', 'Cancel')}
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? safeT('common.saving', 'Saving...') : safeT('common.save', 'Save')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
    </div>
  );
};

export default CardEditorPage; 